// ── @matix/utils/formatters ───────────────────────────────────────────────
//
// Display formatters for POS UI. Money formatting takes either an ISO-like
// currency code string (e.g. 'EGP', 'SAR', 'AED') or a {code, precision}
// object — the object form is preferred so per-currency precision from the
// backend (mstr_Currencies.CurrencyPrecision) is honored.

export interface CurrencyLike {
  code: string;
  precision?: number;
}

const DEFAULT_CURRENCY_CODE = 'EGP';
const DEFAULT_PRECISION = 2;
const DEFAULT_LOCALE = 'en-US';

function toCurrencyLike(c: string | CurrencyLike | undefined): CurrencyLike {
  if (!c) return { code: DEFAULT_CURRENCY_CODE, precision: DEFAULT_PRECISION };
  if (typeof c === 'string') return { code: c, precision: DEFAULT_PRECISION };
  return { code: c.code, precision: c.precision ?? DEFAULT_PRECISION };
}

/**
 * Format a money amount as "<number with precision> <code>" (e.g. "12.50 EGP",
 * "1.250 KWD"). The trailing-code style matches what receipts and totals
 * already use throughout the apps; we deliberately don't use
 * `Intl.NumberFormat({ style: 'currency' })` because (a) it forces the locale's
 * symbol position, and (b) older browsers' symbol tables don't cover every
 * Matix tenant currency.
 *
 * Pass `currency` as either a string code or `{ code, precision }`. Passing
 * a string degrades gracefully to 2 decimals.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | CurrencyLike | undefined = DEFAULT_CURRENCY_CODE,
  locale: string = DEFAULT_LOCALE,
): string {
  const { code, precision } = toCurrencyLike(currency);
  const value = Number.isFinite(amount) ? Number(amount) : 0;
  const digits = Math.max(0, Math.min(8, precision ?? DEFAULT_PRECISION));
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
  return `${formatted} ${code}`;
}

/**
 * Format just the numeric amount (no code) using the currency's precision.
 * Use this in compact contexts where the column header already shows the
 * code (catalog tables, the menu price strip).
 */
export function formatAmount(
  amount: number | null | undefined,
  currency: string | CurrencyLike | undefined = DEFAULT_CURRENCY_CODE,
  locale: string = DEFAULT_LOCALE,
): string {
  const { precision } = toCurrencyLike(currency);
  const value = Number.isFinite(amount) ? Number(amount) : 0;
  const digits = Math.max(0, Math.min(8, precision ?? DEFAULT_PRECISION));
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatQty(
  qty: number | null | undefined,
  locale: string = DEFAULT_LOCALE,
  fractionDigits: number = 3,
): string {
  const value = qty ?? 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatPercent(
  value: number | null | undefined,
  locale: string = DEFAULT_LOCALE,
  fractionDigits: number = 1,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format((value ?? 0) / 100);
}

/**
 * The legacy ERP stores datetime columns as naive (no-timezone) strings in
 * the tenant's local wall-clock — e.g. `'2026-05-08T08:18:43.000'`. The
 * `entry_date` field on every order response is that exact shape.
 *
 * `new Date('2026-05-08T08:18:43.000')` parses to **local-time of the host**
 * (V8 behavior for naive ISO strings without a `Z` or offset). That is wrong
 * here: when the cashier's PC and the tenant timezone agree (the common case),
 * we want the components rendered verbatim. The detector below catches the
 * naive shape and returns the parts directly so reformatting stays accurate
 * regardless of the host's clock.
 */
const NAIVE_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?$/;

interface DateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function partsFromInput(input: string | Date): DateParts | null {
  if (typeof input === 'string') {
    const m = NAIVE_DATETIME_RE.exec(input.trim());
    if (m) {
      return {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour: Number(m[4]),
        minute: Number(m[5]),
      };
    }
  }
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return null;
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

function to12Hour(hour24: number): { hour: number; suffix: 'AM' | 'PM' } {
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour: h12, suffix };
}

export function formatDateTime(
  iso: string | Date | null | undefined,
  _locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return '';
  const p = partsFromInput(iso);
  if (!p) return '';
  const { hour, suffix } = to12Hour(p.hour);
  const yyyy = String(p.year).padStart(4, '0');
  const MM = String(p.month).padStart(2, '0');
  const dd = String(p.day).padStart(2, '0');
  const hh = String(hour).padStart(2, '0');
  const mm = String(p.minute).padStart(2, '0');
  return `${yyyy}-${MM}-${dd} ${hh}:${mm} ${suffix}`;
}

export function formatTime(
  iso: string | Date | null | undefined,
  _locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return '';
  const p = partsFromInput(iso);
  if (!p) return '';
  const { hour, suffix } = to12Hour(p.hour);
  const hh = String(hour).padStart(2, '0');
  const mm = String(p.minute).padStart(2, '0');
  return `${hh}:${mm} ${suffix}`;
}

export function formatDate(
  iso: string | Date | null | undefined,
  _locale: string = DEFAULT_LOCALE,
): string {
  if (!iso) return '';
  const p = partsFromInput(iso);
  if (!p) return '';
  const yyyy = String(p.year).padStart(4, '0');
  const MM = String(p.month).padStart(2, '0');
  const dd = String(p.day).padStart(2, '0');
  return `${yyyy}-${MM}-${dd}`;
}

/** Elapsed time since `from`, formatted as "Xm Ys" — used by KDS timers. */
export function formatElapsed(from: string | Date): string {
  const start = typeof from === 'string' ? new Date(from) : from;
  const seconds = Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
}

/** Compact order number formatting: "ORD-2026-00042" */
export function formatOrderNumber(n: number, year: number = new Date().getFullYear()): string {
  return `ORD-${year}-${n.toString().padStart(5, '0')}`;
}

/** Generate a UUID v4 — used for idempotency keys. */
export function uuidV4(): string {
  // Browser native when available
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback (older browsers)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
